"use client"

import { FileUpload, useUploadCVs } from '@yeshuman/sdk'
import type { FileWithProgress } from '@yeshuman/sdk'
import * as React from 'react'
import { toast } from 'sonner'

const CV_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
}

export function CVUploadFields() {
  const { uploadCVs, isUploading, uploadError } = useUploadCVs()
  const [files, setFiles] = React.useState<FileWithProgress[]>([])

  const handleFilesSelected = React.useCallback(
    async (selectedFiles: File[]) => {
      const newFiles: FileWithProgress[] = selectedFiles.map((file) => ({
        file,
        progress: 0,
        status: 'pending',
      }))
      setFiles((prev) => [...prev, ...newFiles])

      try {
        setFiles((prev) =>
          prev.map((f) =>
            selectedFiles.includes(f.file) ? { ...f, status: 'uploading', progress: 0 } : f,
          ),
        )

        await uploadCVs(selectedFiles)

        setFiles((prev) =>
          prev.map((f) =>
            selectedFiles.includes(f.file) ? { ...f, status: 'completed', progress: 100 } : f,
          ),
        )

        toast.success(`Successfully uploaded ${selectedFiles.length} CV(s)`)
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) =>
            selectedFiles.includes(f.file)
              ? {
                  ...f,
                  status: 'error',
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : f,
          ),
        )

        toast.error(
          `Failed to upload CV(s): ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
      }
    },
    [uploadCVs],
  )

  const handleFileRemove = React.useCallback((file: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== file))
  }, [])

  React.useEffect(() => {
    if (uploadError) {
      toast.error(`Upload error: ${uploadError.message}`)
    }
  }, [uploadError])

  return (
    <FileUpload
      accept={CV_ACCEPT}
      maxSize={10 * 1024 * 1024}
      multiple
      onFilesSelected={handleFilesSelected}
      onFileRemove={handleFileRemove}
      files={files}
      disabled={isUploading}
    />
  )
}
